/**
 * Phase 34 Session 34.3 — TelemetryClient.
 *
 * Framework-agnostic SDK that batches `pageView()` + `track()` calls and
 * flushes them in arrays of `PageViewInput` / `UiEventInput` (defined in
 * `@commerce-os/shared-types`) to the backend ingest endpoints.
 *
 * Triggers:
 *   - **Size**: any single enqueue that pushes the buffer to `sizeLimit` (50)
 *     fires an immediate flush.
 *   - **Time**: a sliding 5s timer flushes whatever's buffered. Cancelled +
 *     re-armed on every enqueue so quiet periods stay efficient.
 *   - **Visibility**: consumers wire a `visibilitychange` / app-background
 *     listener to call `flush({ beacon: true })` so a tab close doesn't drop
 *     the last few events.
 *
 * Transport: the consumer injects a `transport` callback so the SDK stays
 * Axios-/fetch-/sendBeacon-agnostic. The dashboard wraps `axios.post`; the
 * POS wraps `fetch` with the offline-queue retry semantics layered on top.
 *
 * Errors: the SDK NEVER throws. Transport failures route the batch back to
 * the head of the queue for the next flush attempt; unrecoverable errors
 * (e.g. 4xx response) are dropped after a single retry to prevent a poison
 * pill from blocking the queue forever.
 */
import type {
  PageViewInput,
  UiEventInput,
} from '@commerce-os/shared-types';
import { TelemetryQueue, type PersistenceAdapter } from './queue';
import type { TelemetryEventName } from './event-names';

export interface TelemetryTransportPayload {
  pageViews: PageViewInput[];
  uiEvents: UiEventInput[];
  /** When true, the consumer should prefer `navigator.sendBeacon` (web) or
   *  the equivalent fire-and-forget primitive. The SDK sets this on the
   *  final flush triggered by `visibilitychange=hidden` / app-background. */
  beacon?: boolean;
}

export type TelemetryTransport = (
  payload: TelemetryTransportPayload,
) => Promise<TelemetryTransportResult>;

export type TelemetryTransportResult =
  | { ok: true }
  | { ok: false; retryable: boolean; reason?: string };

export interface TelemetryClientOptions {
  transport: TelemetryTransport;
  /** Per-channel size trigger. Default 50. */
  batchSize?: number;
  /** Quiet-period flush interval in ms. Default 5000. */
  flushIntervalMs?: number;
  /** Optional persistence — POS injects an AsyncStorage adapter so an
   *  offline shift survives device reload. Dashboard skips. */
  pageViewPersistence?: PersistenceAdapter<PageViewInput>;
  uiEventPersistence?: PersistenceAdapter<UiEventInput>;
  /** Per-batch hard cap on dropped-oldest before forcing a flush. Default 5000. */
  hardCap?: number;
  /** Logger hook for transport failures (consumer-supplied; the SDK never
   *  prints to console directly). */
  onError?: (err: unknown, payload: TelemetryTransportPayload) => void;
  /** Source of `now()` — injectable for tests. */
  now?: () => number;
  /** Source of `setTimeout` / `clearTimeout` — injectable for tests. */
  scheduler?: {
    setTimeout: (fn: () => void, ms: number) => unknown;
    clearTimeout: (handle: unknown) => void;
  };
}

const DEFAULT_BATCH_SIZE = 50;
const DEFAULT_FLUSH_INTERVAL_MS = 5_000;

export class TelemetryClient {
  private readonly transport: TelemetryTransport;
  private readonly batchSize: number;
  private readonly flushIntervalMs: number;
  private readonly onError?: (
    err: unknown,
    payload: TelemetryTransportPayload,
  ) => void;
  private readonly setTimeoutFn: (fn: () => void, ms: number) => unknown;
  private readonly clearTimeoutFn: (handle: unknown) => void;

  private pageViews: TelemetryQueue<PageViewInput>;
  private uiEvents: TelemetryQueue<UiEventInput>;

  private timer: unknown = null;
  private actorId: string | null = null;
  private clientSessionId: string | null = null;
  private deviceId: string | null = null;
  private flushInFlight: Promise<void> | null = null;
  private destroyed = false;

  constructor(opts: TelemetryClientOptions) {
    this.transport = opts.transport;
    this.batchSize = Math.max(1, opts.batchSize ?? DEFAULT_BATCH_SIZE);
    this.flushIntervalMs = Math.max(
      100,
      opts.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS,
    );
    this.onError = opts.onError;

    const scheduler = opts.scheduler;
    this.setTimeoutFn = scheduler
      ? scheduler.setTimeout
      : (fn, ms) => globalThis.setTimeout(fn, ms);
    this.clearTimeoutFn = scheduler
      ? scheduler.clearTimeout
      : (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>);

    this.pageViews = new TelemetryQueue<PageViewInput>({
      sizeLimit: this.batchSize,
      hardCap: opts.hardCap,
      persistence: opts.pageViewPersistence,
    });
    this.uiEvents = new TelemetryQueue<UiEventInput>({
      sizeLimit: this.batchSize,
      hardCap: opts.hardCap,
      persistence: opts.uiEventPersistence,
    });
  }

  /** Hydrate persisted queues. Must be called once at boot when persistence
   *  is configured; idempotent + safe to call when it isn't. */
  async hydrate(): Promise<void> {
    await Promise.all([this.pageViews.hydrate(), this.uiEvents.hydrate()]);
  }

  /** Set / clear actor metadata stamped onto subsequent events. The backend
   *  auth-context interceptor still derives the authoritative actorId from
   *  the JWT; client-side actor is supplementary (e.g. for offline queue
   *  rows that haven't been forwarded yet). */
  setActor(actorId: string | null): void {
    this.actorId = actorId;
  }

  setClientSessionId(sessionId: string | null): void {
    this.clientSessionId = sessionId;
  }

  setDeviceId(deviceId: string | null): void {
    this.deviceId = deviceId;
  }

  pageView(
    route: string,
    extra?: Partial<Pick<PageViewInput, 'params' | 'referrer' | 'durationMs'>>,
  ): void {
    if (this.destroyed) return;
    const payload: PageViewInput = {
      route,
      viewedAt: new Date().toISOString(),
      params: extra?.params ?? null,
      referrer: extra?.referrer ?? null,
      durationMs: extra?.durationMs ?? null,
      clientSessionId: this.clientSessionId,
      deviceId: this.deviceId,
    };
    void this.actorId; // Reserved for consumer-side debug surfaces.
    const sizeHit = this.pageViews.enqueue(payload);
    if (sizeHit) {
      void this.flush();
    } else {
      this.armTimer();
    }
  }

  track(
    eventName: TelemetryEventName | string,
    extra?: Partial<Pick<UiEventInput, 'context' | 'route'>>,
  ): void {
    if (this.destroyed) return;
    const payload: UiEventInput = {
      eventName,
      occurredAt: new Date().toISOString(),
      context: extra?.context ?? null,
      route: extra?.route ?? null,
      clientSessionId: this.clientSessionId,
      deviceId: this.deviceId,
    };
    const sizeHit = this.uiEvents.enqueue(payload);
    if (sizeHit) {
      void this.flush();
    } else {
      this.armTimer();
    }
  }

  /** Drain both queues + send via transport. Concurrent calls collapse onto
   *  the in-flight promise to avoid double-sends. */
  async flush(opts: { beacon?: boolean } = {}): Promise<void> {
    if (this.flushInFlight) {
      return this.flushInFlight;
    }
    this.cancelTimer();

    const pageViews = this.pageViews.drain();
    const uiEvents = this.uiEvents.drain();
    if (pageViews.length === 0 && uiEvents.length === 0) {
      return;
    }

    const payload: TelemetryTransportPayload = {
      pageViews,
      uiEvents,
      beacon: opts.beacon === true,
    };

    this.flushInFlight = (async () => {
      try {
        const result = await this.transport(payload);
        if (!result.ok) {
          if (result.retryable) {
            // Re-queue for the next attempt — preserves rough chronological
            // order via prepend so reports stay coherent.
            this.pageViews.prepend(pageViews);
            this.uiEvents.prepend(uiEvents);
            this.armTimer();
          } else {
            // Non-retryable (e.g. 401, 4xx validation): drop the batch.
            // Surface to the consumer's logger if one was injected.
            this.onError?.(
              new Error(result.reason ?? 'Telemetry transport rejected batch'),
              payload,
            );
          }
        }
      } catch (err) {
        this.pageViews.prepend(pageViews);
        this.uiEvents.prepend(uiEvents);
        this.onError?.(err, payload);
        this.armTimer();
      } finally {
        this.flushInFlight = null;
      }
    })();
    return this.flushInFlight;
  }

  /** Returns the current buffer sizes — used by tests + the optional
   *  consumer-side debug surface. */
  getQueueSizes(): { pageViews: number; uiEvents: number } {
    return {
      pageViews: this.pageViews.size(),
      uiEvents: this.uiEvents.size(),
    };
  }

  /** Cancel any pending timer + mark as destroyed so future calls no-op.
   *  Does NOT auto-flush — call `flush()` explicitly first if you want to
   *  drain on teardown. */
  destroy(): void {
    this.destroyed = true;
    this.cancelTimer();
  }

  private armTimer(): void {
    if (this.timer !== null || this.destroyed) return;
    this.timer = this.setTimeoutFn(() => {
      this.timer = null;
      void this.flush();
    }, this.flushIntervalMs);
  }

  private cancelTimer(): void {
    if (this.timer !== null) {
      this.clearTimeoutFn(this.timer);
      this.timer = null;
    }
  }
}
